function generateCustomizeGroupId(length = 7) {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  let groupId = '';
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    for (let i = 0; i < length; i += 1) {
      groupId += alphabet[bytes[i] % alphabet.length];
    }
    return groupId;
  }
  for (let i = 0; i < length; i += 1) {
    groupId += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
  }
  return groupId;
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        const customizeError = this.buyboxCustomizeSubmitError();
        if (customizeError) {
          this.handleErrorMessage(customizeError);
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (this.submitButton.hasAttribute('disabled')) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const afterAdd = this.dataset.afterAdd;
        const goToCheckout = afterAdd === 'checkout';
        const useCartDrawer = afterAdd === 'cart_drawer';
        const shouldClearCart = this.dataset.clearCart === 'true';
        const cartUi = useCartDrawer || (!afterAdd && this.cart) ? this.cart : null;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const mainVariantId = formData.get('id');
        const subscriptionVariantId = this.submitButton.dataset.subscriptionProductVaraintId;
        const addSubscriptionBundle =
          subscriptionVariantId && String(subscriptionVariantId) !== String(mainVariantId);
        const committedEngravingBtn = this.findCommittedEngravingButton();
        const buyboxEngravingLine = this.buyboxEngravingLineItem(mainVariantId);
        const addEngraving = Boolean(committedEngravingBtn?.dataset?.variantId) || Boolean(buyboxEngravingLine);
        const customizePayload = this.buyboxCustomizePayload();
        const customizeLines = this.buyboxCustomizeLineItems(mainVariantId);
        const addCustomize = customizeLines.length > 0;
        const excludeMainProduct = customizePayload?.excludeMainProduct === true;
        const useMultiItemJson =
          addSubscriptionBundle || addEngraving || addCustomize || excludeMainProduct;

        if (cartUi) {
          cartUi.setActiveElement(document.activeElement);
        }

        if (useMultiItemJson) {
          config.headers['Content-Type'] = 'application/json';

          const quantity = parseInt(formData.get('quantity') || '1', 10);
          const safeQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
          const properties = this.lineItemPropertiesFromFormData(formData);

          const replacementPrimaryLine = excludeMainProduct
            ? customizeLines.find((line) => line.role === 'replacementPrimary') || customizeLines[0] || null
            : null;
          const customizeChildLines = excludeMainProduct
            ? customizeLines.filter((line) => line !== replacementPrimaryLine)
            : customizeLines;

          const bundleParentId = excludeMainProduct
            ? replacementPrimaryLine?.id
              ? Number(replacementPrimaryLine.id)
              : null
            : Number(mainVariantId);

          const items = [];

          if (!excludeMainProduct) {
            items.push({
              id: Number(mainVariantId),
              quantity: safeQuantity,
              ...(properties ? { properties } : {}),
            });
          } else if (replacementPrimaryLine) {
            const replacementProperties = {
              ...(properties || {}),
              ...(replacementPrimaryLine.properties || {}),
            };
            items.push({
              id: Number(replacementPrimaryLine.id),
              quantity: replacementPrimaryLine.quantity || 1,
              ...(Object.keys(replacementProperties).length ? { properties: replacementProperties } : {}),
            });
          }

          if (addSubscriptionBundle) {
            items.push({
              id: Number(subscriptionVariantId),
              quantity: 1,
              ...(bundleParentId ? { parent_id: bundleParentId } : {}),
              ...(this.submitButton.dataset.sellingPlanId
                ? { selling_plan: Number(this.submitButton.dataset.sellingPlanId) }
                : {}),
            });
          }

          if (buyboxEngravingLine) {
            const engravingCartLine = {
              ...buyboxEngravingLine,
              properties: { ...(buyboxEngravingLine.properties || {}) },
            };
            if (bundleParentId) {
              engravingCartLine.parent_id = bundleParentId;
              const bundleSku = this.buyboxVariantSku(bundleParentId);
              if (bundleSku) engravingCartLine.properties.SKU = bundleSku;
            } else if (mainVariantId) {
              engravingCartLine.parent_id = Number(mainVariantId);
            }
            items.push(engravingCartLine);
          } else if (addEngraving) {
            const engravingLine = this.engravingLineItemFromButton(mainVariantId, committedEngravingBtn);
            if (engravingLine) items.push(engravingLine);
          }

          const customizeGroupId =
            customizeChildLines.length > 0 ? generateCustomizeGroupId() : null;

          customizeChildLines.forEach((line) => {
            const { role: _role, customizeGroup: _customizeGroup, ...cartLine } = line;
            const properties = { ...(cartLine.properties || {}) };
            if (customizeGroupId && line.customizeGroup) {
              properties._group = customizeGroupId;
            }
            items.push({
              ...cartLine,
              ...(Object.keys(properties).length ? { properties } : {}),
              ...(bundleParentId ? { parent_id: bundleParentId } : {}),
            });
          });

          if (items.length === 0) {
            this.handleErrorMessage(
              customizePayload?.validationMessage ||
                'Unable to add items to cart. Please review your selections.'
            );
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.submitButton.removeAttribute('aria-disabled');
            return;
          }

          const payload = { items };
          if (cartUi && !goToCheckout) {
            payload.sections = cartUi.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
          }
          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];
          if (cartUi && !goToCheckout) {
            formData.append(
              'sections',
              cartUi
                .getSectionsToRender()
                .map((section) => section.id)
                .join(',')
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        const clearCartPromise = shouldClearCart
          ? fetch(`${routes.cart_url}/clear.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
            })
          : Promise.resolve();

        clearCartPromise
          .then(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (goToCheckout) {
              window.location.href = window.routes.checkout_url || '/checkout';
              return;
            }

            if (!cartUi) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      cartUi.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                cartUi.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (cartUi && cartUi.classList.contains('is-empty')) cartUi.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      findCommittedEngravingButton() {
        const wrapper = this.closest('.product__info-wrapper');
        if (!wrapper) return null;
        return wrapper.querySelector('[data-engraving-add].is-engraving-committed');
      }

      buyboxCustomizeRoot() {
        const wrapper = this.closest('.product__info-wrapper') || document;
        return wrapper.querySelector('[data-nw-buybox-customize]');
      }

      buyboxCustomizePayload() {
        const root = this.buyboxCustomizeRoot();
        if (!root?.dataset?.cartPayload) return null;
        try {
          return JSON.parse(root.dataset.cartPayload);
        } catch (e) {
          return null;
        }
      }

      buyboxCustomizeSubmitError() {
        const payload = this.buyboxCustomizePayload();
        if (!payload?.active) return null;
        if (payload.optional) return null;
        if (payload.ready) return null;
        return (
          payload.validationMessage ||
          'Please select your accessory, chain, and charms before adding to cart.'
        );
      }

      buyboxCustomizeLineItems(mainVariantId) {
        const payload = this.buyboxCustomizePayload();
        if (!Array.isArray(payload?.lines)) return [];
        if (!payload.customizeComplete) return [];
        return payload.lines
          .filter((line) => line?.id && Number.isFinite(Number(line.id)))
          .map((line) => {
            const mapped = {
              id: Number(line.id),
              quantity:
                Number.isFinite(Number(line.quantity)) && Number(line.quantity) > 0 ? Number(line.quantity) : 1,
            };
            if (line.role) mapped.role = line.role;
            if (line.customizeGroup) mapped.customizeGroup = true;
            if (line.properties && typeof line.properties === 'object' && Object.keys(line.properties).length) {
              mapped.properties = line.properties;
            }
            return mapped;
          });
      }

      buyboxCustomizeConfig() {
        const root = this.buyboxCustomizeRoot();
        const script = root?.querySelector('[data-nw-customize-config]');
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          return null;
        }
      }

      variantSkuFromCustomizeReplacement(variantId) {
        if (!variantId) return '';
        const config = this.buyboxCustomizeConfig();
        if (!config?.accessories?.length) return '';

        const targetId = Number(variantId);
        if (!Number.isFinite(targetId)) return '';

        for (const accessory of config.accessories) {
          const variants = accessory?.pendantReplacement?.variants;
          if (!Array.isArray(variants)) continue;
          const match = variants.find((entry) => Number(entry?.variantId) === targetId);
          if (match?.sku) return String(match.sku).trim();
        }

        return '';
      }

      buyboxVariantSku(variantId) {
        const wrapper = this.closest('.product__info-wrapper') || document;
        const productInfo = this.closest('product-info') || wrapper.querySelector('product-info');
        const id = variantId != null ? String(variantId) : '';

        if (id) {
          const customizeSku = this.variantSkuFromCustomizeReplacement(id);
          if (customizeSku) return customizeSku;

          const buyboxInput = wrapper.querySelector(
            '[data-nw-buybox-color] [data-nw-variant-input][value="' + id + '"]'
          );
          const buyboxSku = buyboxInput?.dataset?.variantSku?.trim();
          if (buyboxSku) return buyboxSku;
        }

        if (productInfo?.getSelectedVariant) {
          const variant = productInfo.getSelectedVariant(productInfo);
          if (variant?.sku && (!id || String(variant.id) === id)) {
            return String(variant.sku).trim();
          }
        }

        const checkedBuybox = wrapper.querySelector('[data-nw-buybox-color] [data-nw-variant-input]:checked');
        const checkedSku = checkedBuybox?.dataset?.variantSku?.trim();
        if (checkedSku) return checkedSku;

        return '';
      }

      buyboxParentVariantSku(mainVariantId) {
        return this.buyboxVariantSku(mainVariantId);
      }

      buyboxEngravingLineItem(mainVariantId) {
        const wrapper = this.closest('.product__info-wrapper') || document;
        const root = wrapper.querySelector('[data-nw-buybox-engraving]');
        if (!root?.dataset?.variantId) return null;

        const inputs = Array.from(root.querySelectorAll('[data-nw-engraving-input]')).sort(
          (a, b) => Number(a.dataset.nwEngravingLine || 0) - Number(b.dataset.nwEngravingLine || 0)
        );
        const line1 = (inputs[0]?.value ?? '').trim();
        const line2 = (inputs[1]?.value ?? '').trim();
        if (!line1 && !line2) return null;

        const id = Number(root.dataset.variantId);
        if (!Number.isFinite(id)) return null;

        const fontSelect = root.querySelector('[data-nw-engraving-font]');
        const fontLabel =
          fontSelect?.selectedOptions?.[0]?.textContent?.trim() ||
          fontSelect?.value ||
          '';

        const parentSku = this.buyboxParentVariantSku(mainVariantId);
        const properties = {
          SKU: parentSku,
          Font: fontLabel,
          Line1: line1,
          Line2: line2,
        };

        return {
          id,
          quantity: 1,
          properties,
        };
      }

      engravingLineItemFromButton(mainVariantId, engravingBtn) {
        if (!engravingBtn?.dataset?.variantId) return null;
        const id = Number(engravingBtn.dataset.variantId);
        if (!Number.isFinite(id)) return null;
        const root = engravingBtn.closest('[data-engraving-addon]');
        const input = root?.querySelector('[data-engraving-input]');
        const engravingText = (input?.value ?? '').trim();
        return {
          id,
          quantity: 1,
          parent_id: Number(mainVariantId),
          properties: {
            Engraving: engravingText,
          },
        };
      }

      lineItemPropertiesFromFormData(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (!key.startsWith('properties[')) continue;
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        }
        return Object.keys(properties).length ? properties : null;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent =
            this.submitButton.getAttribute('data-cta-label') || window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
